import api from "@/api/api";
import { useState } from "react";
import footerBg from "../../public/svg/obst-bg.svg";
import Modal from "@/components/UI/Modal";
import { validateMunicipalitiesForm } from "@/helpers/validation";
import SeoHelmet from "@/components/UI/SeoHelmet";
import seoImg from "@/public/img/seo/gemeindeformular.png";
import {
    LOCAL_SUPPLIES_OPTIONS,
    MOTIVATION_OPTIONS,
    PROJECT_START_OPTIONS,
    PARTNER_TALKS_OPTIONS,
} from "@/models/customersTypes";
import type { MunicipalityUiForm as UIForm } from "@/helpers/validation";
import {
    validateMunicipalityUiForm,
    mapMunicipalityUiToDto,
    inputClassMunicipality,
    OPTIONAL_TEXT_FIELDS_MUNICIPALITY,
    MUNICIPALITY_DEFAULT_UI_FORM,
    sanitizePostalCode,
    parsePopulationValue,
    normalizeOptional,
    stepPopulation,
    POPULATION_STEP,
} from "@/helpers/validation";
import { track } from '@/helpers/analytics';

const defaultForm: UIForm = MUNICIPALITY_DEFAULT_UI_FORM;

const isOptional = (name: keyof UIForm) =>
    (OPTIONAL_TEXT_FIELDS_MUNICIPALITY as readonly (keyof UIForm)[]).includes(name);

export default function MunicipalitiesInterestFormPage() {
    const [form, setForm] = useState<UIForm>(defaultForm);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [touched, setTouched] = useState<Record<string, boolean>>({});
    const [showModal, setShowModal] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [showErrors, setShowErrors] = useState(false);

    const handleChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const t = e.currentTarget;
        const { name } = t;

        if (t instanceof HTMLInputElement && t.type === "checkbox") {
            if (name === "sales_area_available") {
                const checked = t.checked;
                setForm((p: UIForm) => ({
                    ...p,
                    sales_area_available: checked,
                    sales_area_location: checked ? p.sales_area_location : null,
                }));
                setTouched((p: Record<string, boolean>) => ({ ...p, [name]: true }));
                setErrors(() => validateMunicipalityUiForm({ ...form, sales_area_available: checked } as UIForm));
            }
            return;
        }

        let value = t.value;

        if (name === "postal_code") value = sanitizePostalCode(value);

        if (name === "population") {
            const nextForm: UIForm = { ...form, population: parsePopulationValue(value) };
            setForm(nextForm);
            setTouched((p: Record<string, boolean>) => ({ ...p, [name]: true }));
            setErrors(() => validateMunicipalityUiForm(nextForm));
            return;
        }

        const nextForm: UIForm = normalizeOptional(form, name as keyof UIForm, value);
        setForm(nextForm);
        setTouched((p: Record<string, boolean>) => ({ ...p, [name]: true }));
        setErrors(() => validateMunicipalityUiForm(nextForm));
    };

    const handleBlur = (
        e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
    ) => {
        const { name } = e.currentTarget;
        setTouched((p: Record<string, boolean>) => ({ ...p, [name]: true }));
        setErrors(() => validateMunicipalityUiForm(form));
    };

    const handleArrayChange = (
        name: "local_supplies" | "motivation",
        option: string,
        checked: boolean
    ) => {
        setForm((prev: UIForm) => {
            const s = new Set(prev[name] as string[]);
            if (checked) s.add(option);
            else s.delete(option);

            const nextForm = { ...prev, [name]: Array.from(s) } as UIForm;

            setTouched((t: Record<string, boolean>) => ({ ...t, [name]: true }));
            setErrors(() => validateMunicipalityUiForm(nextForm));

            return nextForm;
        });
    };

    const handlePopulationKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "ArrowUp" || e.key === "ArrowDown") {
            e.preventDefault();
            setForm((p: UIForm) => ({
                ...p,
                population: stepPopulation(p.population, e.key === "ArrowUp" ? "up" : "down"),
            }));
        }
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (submitting) return;

        setShowErrors(true);

        const uiErrors = validateMunicipalityUiForm(form);
        setErrors(uiErrors);
        setTouched({
            location: true,
            postal_code: true,
            population: true,
            contact_name: true,
            contact_role: true,
            email: true,
            phone: true,
            local_supplies: true,
            kms_next_supply: true,
            motivation: true,
            motivation_text: true,
            sales_area_available: true,
            sales_area_location: true,
            project_start: true,
            partner_talks: true,
            partner_talks_text: true,
            concrete_wish: true,
        });

        if (Object.keys(uiErrors).length > 0) return;

        const dto = mapMunicipalityUiToDto(form);
        const dtoErrors = validateMunicipalitiesForm(dto);
        if (Object.keys(dtoErrors).length > 0) {
            setErrors((prev) => ({ ...prev, ...dtoErrors }));
            return;
        }

        try {
            setSubmitting(true);
            await api.interests.sendMunicipalitiesForm(dto);
            track('municipality-interest-sent', { project_start: dto.project_start });
            setShowModal(true);
            setForm(defaultForm);
            setTouched({});
            setErrors({});
            setShowErrors(false);
        } catch (err) {
            console.error(err);
            track('form-error', { form: 'municipality-interest' });
            alert("Fehler beim Senden des Formulars.");
        } finally {
            setSubmitting(false);
        }
    };

    const footerSrc =
        (footerBg as unknown as { src?: string }).src ?? (footerBg as unknown as string);

    return (
        <section
            className="w-screen min-h-screen bg-cover bg-center relative flex flex-col"
            style={{ backgroundImage: `url(${footerSrc})` }}
        >
            <SeoHelmet
                title="Interessentenformular für Gemeinden"
                description="Füllen Sie unser Interessentenformular für Gemeinden aus und wir nehmen Kontakt mit Ihnen auf."
                image={seoImg}
                url="https://www.yobsti.de/gemeindeformular"
            />

            <div className="w-full flex justify-center mt-6 mb-4">
                <img
                    src="/logo.svg"
                    alt="Yobsti Logo"
                    className="h-12 w-auto select-none"
                    loading="eager"
                    onError={(e) => {
                        (e.currentTarget as HTMLImageElement).style.display = "none";
                    }}
                />
            </div>

            <div className="flex-1 w-full">
                <div className="max-w-5xl mx-auto px-4 h-full grid place-items-center">
                    <div className="w-full">
                        <div className="text-center space-y-4 mb-6 md:mb-10">
                            <h1 className="text-3xl md:text-4xl font-bold">Interessentenformular für Gemeinden</h1>
                            <div className="text-base md:text-lg leading-relaxed text-gray-800 space-y-2">
                                <p>Viele kleine Orte suchen nach einer neuen Form des Einkaufens – flexibel und barrierefrei.</p>
                                <p>Yobsti unterstützt Gemeinden dabei, moderne Dorfläden zu realisieren: digital gesteuert, lokal verankert. Wenn auch Ihre Kommune Interesse hat, freuen wir uns über ein paar Informationen im folgenden Formular. Wir melden uns persönlich bei Ihnen zurück.</p>
                            </div>
                        </div>

                        <div className="max-w-5xl mx-auto px-0 pb-20">
                            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 md:p-10 space-y-8">
                                <h2 className="text-xl md:text-2xl font-semibold">Gemeinde-Formular</h2>

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Ort / Standort</label>
                                        <input
                                            type="text"
                                            name="location"
                                            value={form.location}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.location, !!errors.location && showErrors, !!touched.location)}
                                            required
                                        />
                                        {showErrors && errors.location && <p className="text-red text-sm mt-1">{errors.location}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">PLZ</label>
                                        <input
                                            type="text"
                                            name="postal_code"
                                            value={form.postal_code}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.postal_code, !!errors.postal_code && showErrors, !!touched.postal_code) + " appearance-none"}
                                            placeholder="z. B. 76133"
                                            maxLength={5}
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            required
                                            aria-invalid={showErrors && !!errors.postal_code}
                                        />
                                        {showErrors && errors.postal_code && <p className="text-red text-sm mt-1">{errors.postal_code}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Einwohnerzahl</label>
                                        <input
                                            type="number"
                                            name="population"
                                            value={form.population}
                                            onChange={handleChange}
                                            onKeyDown={handlePopulationKeyDown}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.population, !!errors.population && showErrors, !!touched.population) + " appearance-none"}
                                            min={1}
                                            step={POPULATION_STEP}
                                            inputMode="numeric"
                                            required
                                            aria-invalid={showErrors && !!errors.population}
                                        />
                                        {showErrors && errors.population && <p className="text-red text-sm mt-1">{errors.population}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Kontaktperson – Name</label>
                                        <input
                                            type="text"
                                            name="contact_name"
                                            value={form.contact_name}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.contact_name, !!errors.contact_name && showErrors, !!touched.contact_name)}
                                            required
                                        />
                                        {showErrors && errors.contact_name && <p className="text-red text-sm mt-1">{errors.contact_name}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Kontaktperson – Funktion</label>
                                        <input
                                            type="text"
                                            name="contact_role"
                                            value={form.contact_role}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.contact_role, !!errors.contact_role && showErrors, !!touched.contact_role)}
                                            required
                                        />
                                        {showErrors && errors.contact_role && <p className="text-red text-sm mt-1">{errors.contact_role}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">E-Mail</label>
                                        <input
                                            type="email"
                                            name="email"
                                            value={form.email}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.email, !!errors.email && showErrors, !!touched.email)}
                                            required
                                        />
                                        {showErrors && errors.email && <p className="text-red text-sm mt-1">{errors.email}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Telefon (optional)</label>
                                        <input
                                            type="tel"
                                            name="phone"
                                            value={form.phone ?? ""}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.phone ?? "", !!errors.phone && showErrors, !!touched.phone)}
                                            placeholder="+49 …"
                                            data-optional={isOptional("phone")}
                                        />
                                        {showErrors && errors.phone && <p className="text-red text-sm mt-1">{errors.phone}</p>}
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Entfernung zum nächsten Supermarkt (optional)</label>
                                        <input
                                            type="text"
                                            name="kms_next_supply"
                                            value={form.kms_next_supply ?? ""}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.kms_next_supply ?? "", !!errors.kms_next_supply && showErrors, !!touched.kms_next_supply)}
                                            placeholder="z. B. 5 km / 10 min"
                                            data-optional={isOptional("kms_next_supply")}
                                        />
                                        {showErrors && errors.kms_next_supply && <p className="text-red text-sm mt-1">{errors.kms_next_supply}</p>}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className={"block text-sm font-medium p-1 rounded" + (errors.local_supplies && showErrors ? " text-red" : "")}>
                                        Aktuelle Nahversorgungsangebote
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {LOCAL_SUPPLIES_OPTIONS.map((opt) => (
                                            <label
                                                key={opt}
                                                className={`flex items-center gap-2 border rounded px-3 py-2 ${errors.local_supplies && showErrors ? "border-red text-red" : ""}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={form.local_supplies.includes(opt)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleArrayChange("local_supplies", opt, e.currentTarget.checked)
                                                    }
                                                />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                    {showErrors && errors.local_supplies && <p className="text-red text-sm">{errors.local_supplies}</p>}
                                </div>

                                <div className="space-y-3">
                                    <label className={"block text-sm font-medium p-1 rounded" + (errors.motivation && showErrors ? " text-red" : "")}>
                                        Motivation
                                    </label>
                                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                        {MOTIVATION_OPTIONS.map((opt) => (
                                            <label
                                                key={opt}
                                                className={`flex items-center gap-2 border rounded px-3 py-2 ${errors.motivation && showErrors ? "border-red text-red" : ""}`}
                                            >
                                                <input
                                                    type="checkbox"
                                                    checked={form.motivation.includes(opt)}
                                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                                        handleArrayChange("motivation", opt, e.currentTarget.checked)
                                                    }
                                                />
                                                {opt}
                                            </label>
                                        ))}
                                    </div>
                                    {showErrors && errors.motivation && <p className="text-red text-sm">{errors.motivation}</p>}
                                </div>

                                {form.motivation.includes("Sonstiges") && (
                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Motivation (Freitext)</label>
                                        <textarea
                                            name="motivation_text"
                                            value={form.motivation_text ?? ""}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            rows={3}
                                            className={inputClassMunicipality(form.motivation_text ?? "", !!errors.motivation_text && showErrors, !!touched.motivation_text)}
                                            data-optional={isOptional("motivation_text")}
                                        />
                                        {showErrors && errors.motivation_text && <p className="text-red text-sm mt-1">{errors.motivation_text}</p>}
                                    </div>
                                )}

                                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Projektstart</label>
                                        <select
                                            name="project_start"
                                            value={form.project_start}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.project_start, !!errors.project_start && showErrors, !!touched.project_start)}
                                        >
                                            {PROJECT_START_OPTIONS.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                        {showErrors && errors.project_start && <p className="text-red text-sm mt-1">{errors.project_start}</p>}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Partnergespräche</label>
                                        <select
                                            name="partner_talks"
                                            value={form.partner_talks}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.partner_talks, !!errors.partner_talks && showErrors, !!touched.partner_talks)}
                                        >
                                            {PARTNER_TALKS_OPTIONS.map((o) => (
                                                <option key={o} value={o}>
                                                    {o}
                                                </option>
                                            ))}
                                        </select>
                                        {showErrors && errors.partner_talks && <p className="text-red text-sm mt-1">{errors.partner_talks}</p>}
                                        {form.partner_talks === "Sonstiges" && (
                                            <div className="md:col-span-2">
                                                <label className="block text-sm font-medium p-1 rounded">Partnergespräche – Details</label>
                                                <textarea
                                                    name="partner_talks_text"
                                                    value={form.partner_talks_text ?? ""}
                                                    onChange={handleChange}
                                                    onBlur={handleBlur}
                                                    rows={3}
                                                    className={inputClassMunicipality(form.partner_talks_text ?? "", !!errors.partner_talks_text && showErrors, !!touched.partner_talks_text)}
                                                    data-optional={isOptional("partner_talks_text")}
                                                />
                                                {showErrors && errors.partner_talks_text && <p className="text-red text-sm mt-1">{errors.partner_talks_text}</p>}
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="space-y-3">
                                    <label className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            name="sales_area_available"
                                            checked={form.sales_area_available}
                                            onChange={handleChange}
                                        />
                                        <span>Verkaufsfläche vorhanden</span>
                                    </label>
                                    {showErrors && errors.sales_area_available && <p className="text-red text-sm">{errors.sales_area_available}</p>}
                                </div>

                                {form.sales_area_available && (
                                    <div>
                                        <label className="block text-sm font-medium p-1 rounded">Lage/Adresse der Verkaufsfläche</label>
                                        <input
                                            type="text"
                                            name="sales_area_location"
                                            value={form.sales_area_location ?? ""}
                                            onChange={handleChange}
                                            onBlur={handleBlur}
                                            className={inputClassMunicipality(form.sales_area_location ?? "", !!errors.sales_area_location && showErrors, !!touched.sales_area_location)}
                                            data-optional={isOptional("sales_area_location")}
                                        />
                                        {showErrors && errors.sales_area_location && <p className="text-red text-sm mt-1">{errors.sales_area_location}</p>}
                                    </div>
                                )}

                                <div>
                                    <label className="block text-sm font-medium p-1 rounded">Konkreter Wunsch (optional)</label>
                                    <textarea
                                        name="concrete_wish"
                                        value={form.concrete_wish ?? ""}
                                        onChange={handleChange}
                                        onBlur={handleBlur}
                                        rows={3}
                                        className={inputClassMunicipality(form.concrete_wish ?? "", !!errors.concrete_wish && showErrors, !!touched.concrete_wish)}
                                        data-optional={isOptional("concrete_wish")}
                                    />
                                    {showErrors && errors.concrete_wish && <p className="text-red text-sm mt-1">{errors.concrete_wish}</p>}
                                </div>

                                <div className="text-center pt-2">
                                    <button
                                        onClick={handleSubmit}
                                        disabled={submitting}
                                        className="bg-tertiary px-8 py-4 rounded-xl text-lg font-bold hover:bg-tertiaryLight transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {submitting ? "Sende ..." : "Unverbindlich Interesse bekunden"}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <Modal show={showModal} onClose={() => setShowModal(false)} />
        </section>
    );
}
